import { useRef, useState } from 'react';
import styled from 'styled-components';
import { Input } from './ui';
import { useSelector } from 'react-redux';
import { uploadFiles } from '../api/services/utilService';
import { registerCS } from '../api/services/customerService';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 30px;
  margin-bottom: 50px;
  padding: 20px 3%;
  border: 1px solid ${(props) => props.theme.colors.subBackground};
  border-radius: 5px;
`;

const TitleContainer = styled.div`
  display: flex;
  width: 80%;
  justify-content: space-between;
  align-items: center;
`;

const InputContainer = styled.div`
  width: 80%;
  border: 1px solid #ccc;
  margin-bottom: 20px;
`;

const MenuTitleText = styled.p`
  font-size: 15px;
  font-weight: 700;
  white-space: pre;
  text-align: center;
`;

const ResizableTextarea = styled.textarea`
  width: 78%;
  padding: 10px;
  margin-bottom: 20px;
  min-height: 150px;
  max-height: 300px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 5px;
  resize: vertical;
  overflow: auto;
  &:focus {
    outline: none;
  }
`;

const FileInputWrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  width: 80%;
  margin-bottom: 20px;
  gap: 1rem;
`;
const FileListWrapper = styled.div`
  width: 80%;
  background-color: #f4f4f4;
  border: 1px solid #ddd;
  border-radius: 10px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  border-bottom: 1px solid #eee;
  padding: 10px;
  &:last-child {
    border-bottom: none;
  }
`;

const FileName = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;

  &::before {
    content: '📄';
  }
`;

const RemoveButton = styled.button`
  border: none;
  background: transparent;
  color: #999;
  cursor: pointer;
  font-size: 16px;

  &:hover {
    color: #d00;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadButton = styled.button`
  padding: 10px;
  max-height: 50px;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.primaryBlue};
  color: white;
  font-weight: 600;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  text-align: center;
  transition: background-color 0.2s ease;

  &:hover {
    opacity: 0.85;
  }
`;

const SubmitButton = styled.button`
  width: 80%;
  padding: 12px;
  background: ${(props) => props.theme.colors.primaryBlue};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  margin-top: 50px;
  &:hover {
    opacity: 0.7;
  }
`;

export default function ContactUsForm({ style }) {
  const fileInputRef = useRef(null);

  const userInfo = useSelector((state) => state.userInfo);

  const [firstName, setFirstName] = useState(userInfo.firstname);
  const [lastName, setLastName] = useState(userInfo.lastname);
  const [email, setEmail] = useState(userInfo.email);
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);

  const handleFileChange = (e) => {
    setFiles([...e.target.files]);
  };

  const handleSubmit = async () => {
    if (message.length > 10) {
      const formData = new FormData();
      formData.append('destination', `custService/${userInfo.idCustomer}`);
      files.forEach((file) => formData.append('files', file));
      formData.append(
        'csInfo',
        JSON.stringify({
          idCustomer: userInfo.idCustomer,
          firstname: firstName,
          lastname: lastName,
          email,
          message,
        }),
      );
      const response = await registerCS(formData);
      if (response.is_success) {
        alert('Submit Successful!');
        window.location.reload();
      }
    } else {
      alert('Please enter a message of at least 10 characters');
    }
  };

  return (
    <Content style={style}>
      <TitleContainer>
        <MenuTitleText>First Name:</MenuTitleText>
        <MenuTitleText>*</MenuTitleText>
      </TitleContainer>
      <InputContainer>
        <Input
          type='text'
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
      </InputContainer>

      <TitleContainer>
        <MenuTitleText>Last Name:</MenuTitleText>
        <MenuTitleText>*</MenuTitleText>
      </TitleContainer>
      <InputContainer>
        <Input
          type='text'
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
      </InputContainer>

      <TitleContainer>
        <MenuTitleText>Your email:</MenuTitleText>
        <MenuTitleText>*</MenuTitleText>
      </TitleContainer>
      <InputContainer>
        <Input
          type='text'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </InputContainer>

      <TitleContainer>
        <MenuTitleText>Message:</MenuTitleText>
        <MenuTitleText>*</MenuTitleText>
      </TitleContainer>
      <ResizableTextarea
        placeholder='Please write a message here'
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />

      <FileInputWrapper>
        {files.length > 0 && (
          <FileListWrapper>
            {files.map((file, idx) => (
              <FileItem key={idx}>
                <FileName>{file.name}</FileName>
                <RemoveButton
                  onClick={() => {
                    const newFiles = [...files];
                    newFiles.splice(idx, 1);
                    setFiles(newFiles);
                  }}
                >
                  ✕
                </RemoveButton>
              </FileItem>
            ))}
          </FileListWrapper>
        )}
        <UploadButton
          onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current.click();
            }
          }}
        >
          📁 Upload Files
        </UploadButton>
        <HiddenFileInput
          id='file-upload'
          type='file'
          ref={fileInputRef}
          multiple={true}
          onChange={handleFileChange}
        />
      </FileInputWrapper>

      <SubmitButton
        onClick={() => {
          handleSubmit();
        }}
      >
        SUBMIT
      </SubmitButton>
    </Content>
  );
}
